import { Report } from '../models/report.model';
import { ORGS_SEED } from '../../../service/data/orgs.seed';

const oname = (id: string) => ORGS_SEED.find(o => o.id === id)!.name;

/**
 * Deterministic stamps — no Date.now(). The listing renders Created At from these.
 *
 * `rep-3` is deliberately cross-org: it draws from Umbrella and the Acme board while being owned
 * by Acme. That is the feature's headline case, so the seed states it rather than leaving every
 * example single-org.
 *
 * The list runs to 14 rows on purpose: the listing paginates at 10, so the seed has to cross that
 * boundary for page controls to appear at all. Participant ids reference real people from the
 * invitations store (Company Summit `bp*`, Board `bd*`, Discovery `pd*`) so opening any row in the
 * wizard still resolves its cohort. `createdBy` varies across a few admins so the "By" column shows
 * a spread of avatars, matching the Invitations listing.
 */
export const REPORTS_SEED: Report[] = [
  {
    id: 'rep-1',
    title: 'Company Summit — Full Cohort Group Profile',
    ownerOrgId: 'r2', ownerOrgName: oname('r2'),
    type: 'group_profile',
    sourceOrgIds: ['r2'],
    participantIds: ['bp1', 'bp2', 'bp3', 'bp4', 'bp5', 'bp6', 'bp7'],
    createdBy: 'Test Owner',
    createdAt: '2026-06-18T00:00:00Z', updatedAt: '2026-06-18T00:00:00Z',
  },
  {
    id: 'rep-2',
    title: 'Board Offsite — Thinking Composition',
    ownerOrgId: 'r1', ownerOrgName: oname('r1'),
    type: 'group_profile_plus',
    sourceOrgIds: ['r1'],
    participantIds: ['bd1', 'bd2', 'bd3', 'bd4', 'bd5', 'bd6'],
    createdBy: 'Test Owner',
    createdAt: '2026-06-24T00:00:00Z', updatedAt: '2026-06-26T00:00:00Z',
  },
  {
    id: 'rep-3',
    title: 'Cross-Org Innovation Cohort — Discovery + Board',
    ownerOrgId: 'r1', ownerOrgName: oname('r1'),
    type: 'team_member_report',
    sourceOrgIds: ['r5', 'r1'],
    participantIds: ['pd1', 'pd3', 'pd5', 'bd2', 'bd4'],
    createdBy: 'Test Owner',
    createdAt: '2026-07-02T00:00:00Z', updatedAt: '2026-07-02T00:00:00Z',
  },
  {
    id: 'rep-4',
    title: 'Q1 Leadership Team — Group Profile',
    ownerOrgId: 'r6', ownerOrgName: oname('r6'),
    type: 'group_profile',
    sourceOrgIds: ['r6'],
    participantIds: ['bp1', 'bp2', 'bp3', 'bp4'],
    createdBy: 'Alex Kim',
    createdAt: '2026-02-11T00:00:00Z', updatedAt: '2026-02-11T00:00:00Z',
  },
  {
    id: 'rep-5',
    title: 'Product & Design Guild — Thinking Composition',
    ownerOrgId: 'r2', ownerOrgName: oname('r2'),
    type: 'group_profile_plus',
    sourceOrgIds: ['r2'],
    participantIds: ['bp1', 'bp2', 'bp3', 'bp4', 'bp5', 'bp6', 'bp7', 'bd1', 'bd2'],
    createdBy: 'Priya Okonkwo',
    createdAt: '2026-03-03T00:00:00Z', updatedAt: '2026-03-05T00:00:00Z',
  },
  {
    id: 'rep-6',
    title: 'Sales Kickoff — Team Member Profile',
    ownerOrgId: 'r4', ownerOrgName: oname('r4'),
    type: 'team_member_report',
    sourceOrgIds: ['r4'],
    participantIds: ['bd1', 'bd2', 'bd3'],
    createdBy: 'Test Owner',
    createdAt: '2026-03-19T00:00:00Z', updatedAt: '2026-03-19T00:00:00Z',
  },
  {
    id: 'rep-7',
    title: 'Engineering All-Hands — Full Cohort Group Profile',
    ownerOrgId: 'r2', ownerOrgName: oname('r2'),
    type: 'group_profile',
    sourceOrgIds: ['r2'],
    participantIds: ['bp1', 'bp2', 'bp3', 'bp4', 'bp5', 'bp6', 'bp7', 'bd1', 'bd2', 'bd3', 'bd4', 'bd5'],
    createdBy: 'Alex Kim',
    createdAt: '2026-04-07T00:00:00Z', updatedAt: '2026-04-07T00:00:00Z',
  },
  {
    id: 'rep-8',
    title: 'HR Succession Planning — Thinking Composition',
    ownerOrgId: 'r6', ownerOrgName: oname('r6'),
    type: 'group_profile_plus',
    sourceOrgIds: ['r6'],
    participantIds: ['bp1', 'bp2', 'bp3', 'bp4', 'bp5'],
    createdBy: 'Test Owner',
    createdAt: '2026-04-22T00:00:00Z', updatedAt: '2026-04-24T00:00:00Z',
  },
  {
    id: 'rep-9',
    title: 'Cross-Org Accelerator — Discovery + Summit',
    ownerOrgId: 'r5', ownerOrgName: oname('r5'),
    type: 'team_member_report',
    sourceOrgIds: ['r5', 'r2'],
    participantIds: ['pd1', 'pd3', 'pd5', 'bp1', 'bp2', 'bp3'],
    createdBy: 'Priya Okonkwo',
    createdAt: '2026-05-06T00:00:00Z', updatedAt: '2026-05-06T00:00:00Z',
  },
  {
    id: 'rep-10',
    title: 'Marketing Department — Group Profile',
    ownerOrgId: 'r4', ownerOrgName: oname('r4'),
    type: 'group_profile',
    sourceOrgIds: ['r4'],
    participantIds: ['bd1', 'bd2', 'bd3', 'bd4', 'bd5', 'bd6'],
    createdBy: 'Test Owner',
    createdAt: '2026-05-20T00:00:00Z', updatedAt: '2026-05-20T00:00:00Z',
  },
  {
    id: 'rep-11',
    title: 'Customer Success Cohort — Team Member Profile',
    ownerOrgId: 'r6', ownerOrgName: oname('r6'),
    type: 'team_member_report',
    sourceOrgIds: ['r6'],
    participantIds: ['bp1', 'bp2', 'bp3', 'bp4', 'bp5', 'bp6', 'bp7', 'bd1'],
    createdBy: 'Alex Kim',
    createdAt: '2026-05-29T00:00:00Z', updatedAt: '2026-05-29T00:00:00Z',
  },
  {
    id: 'rep-12',
    title: 'Executive Retreat — Thinking Composition',
    ownerOrgId: 'r4', ownerOrgName: oname('r4'),
    type: 'group_profile_plus',
    sourceOrgIds: ['r4'],
    participantIds: ['bd1', 'bd2', 'bd3', 'bd4'],
    createdBy: 'Test Owner',
    createdAt: '2026-06-05T00:00:00Z', updatedAt: '2026-06-07T00:00:00Z',
  },
  {
    id: 'rep-13',
    title: 'Innovation Lab — Full Cohort Group Profile',
    ownerOrgId: 'r5', ownerOrgName: oname('r5'),
    type: 'group_profile',
    sourceOrgIds: ['r5'],
    participantIds: ['pd1', 'pd3', 'pd5'],
    createdBy: 'Priya Okonkwo',
    createdAt: '2026-06-30T00:00:00Z', updatedAt: '2026-06-30T00:00:00Z',
  },
  {
    id: 'rep-14',
    title: 'Global Design Workshop — Team Member Profile',
    ownerOrgId: 'r2', ownerOrgName: oname('r2'),
    type: 'team_member_report',
    sourceOrgIds: ['r2', 'r1'],
    participantIds: ['bp1', 'bp2', 'bp3', 'bp4', 'bp5', 'bd1', 'bd2'],
    createdBy: 'Test Owner',
    createdAt: '2026-07-15T00:00:00Z', updatedAt: '2026-07-15T00:00:00Z',
  },
];
